import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  BarChart3, Users, Mail, Send, Clock, AlertCircle, 
  CheckCircle, XCircle, ArrowLeft, RefreshCw, TrendingUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [queue, setQueue] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [triggerEmail, setTriggerEmail] = useState('');
  const [triggerResult, setTriggerResult] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async () => {
    setRefreshing(true);
    try {
      const [statsRes, subsRes, logsRes, queueRes] = await Promise.all([
        fetch(`${API_URL}/api/emails/stats`),
        fetch(`${API_URL}/api/emails/subscribers?limit=100`),
        fetch(`${API_URL}/api/emails/logs?limit=50`),
        fetch(`${API_URL}/api/emails/queue`)
      ]);
      const [statsData, subsData, logsData, queueData] = await Promise.all([
        statsRes.json(), subsRes.json(), logsRes.json(), queueRes.json()
      ]);
      setStats(statsData.data);
      setSubscribers(subsData.data || []);
      setLogs(logsData.data || []);
      setQueue(queueData.data);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
    } finally {
      setIsLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleProcessQueue = async () => {
    try {
      const res = await fetch(`${API_URL}/api/emails/process-queue`, { method: 'POST' });
      const data = await res.json();
      setTriggerResult(`Queue processed: ${data.processed} sent, ${data.errors} errors`);
      fetchData();
    } catch (err) {
      setTriggerResult('Error processing queue');
    }
  };

  const handleSendEmail = async (type) => {
    if (!triggerEmail || !triggerEmail.includes('@')) {
      setTriggerResult('Please enter a valid email address');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/emails/${type}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: triggerEmail })
      });
      const data = await res.json();
      setTriggerResult(`${type}: ${data.status || data.message || 'sent'}`);
      fetchData();
    } catch (err) {
      setTriggerResult(`Error sending ${type}`);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'subscribers', label: 'Subscribers', icon: Users },
    { id: 'logs', label: 'Email Logs', icon: Mail },
    { id: 'queue', label: 'Queue', icon: Clock },
    { id: 'trigger', label: 'Send Emails', icon: Send },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#1a1a2e] flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-10 h-10 text-[#6B7A5C] animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1a2e] text-white">
      {/* Header */}
      <header className="bg-[#16213e] border-b border-[#2a2a4a] px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-gray-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-xl font-bold">
              <span className="text-white">Spend</span>
              <span className="text-[#6B7A5C]">Less</span>
              <span className="text-gray-500 font-normal ml-2">Admin</span>
            </h1>
          </div>
          <Button
            onClick={fetchData}
            disabled={refreshing}
            className="bg-[#2a2a4a] hover:bg-[#3a3a5a] text-gray-300 border border-[#3a3a5a] text-sm"
            data-testid="admin-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-[#16213e] rounded-lg p-1 overflow-x-auto" data-testid="admin-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-[#6B7A5C] text-white'
                  : 'text-gray-400 hover:text-white hover:bg-[#2a2a4a]'
              }`}
              data-testid={`admin-tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div data-testid="admin-overview-panel">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Card className="bg-[#16213e] border-[#2a2a4a] p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-lg bg-[#6B7A5C] bg-opacity-20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-[#6B7A5C]" />
                  </div>
                  <span className="text-gray-400 text-sm">Subscribers</span>
                </div>
                <p className="text-3xl font-bold text-white" data-testid="stat-subscribers">{stats.total_subscribers}</p>
              </Card>
              <Card className="bg-[#16213e] border-[#2a2a4a] p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-lg bg-[#C17855] bg-opacity-20 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-[#C17855]" />
                  </div>
                  <span className="text-gray-400 text-sm">Purchasers</span>
                </div>
                <p className="text-3xl font-bold text-white" data-testid="stat-purchasers">{stats.total_purchasers}</p>
              </Card>
              <Card className="bg-[#16213e] border-[#2a2a4a] p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-lg bg-blue-500 bg-opacity-20 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-blue-400" />
                  </div>
                  <span className="text-gray-400 text-sm">Emails Sent</span>
                </div>
                <p className="text-3xl font-bold text-white" data-testid="stat-emails-sent">{stats.total_sent}</p>
              </Card>
              <Card className="bg-[#16213e] border-[#2a2a4a] p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-lg bg-emerald-500 bg-opacity-20 flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-emerald-400" />
                  </div>
                  <span className="text-gray-400 text-sm">Conversion</span>
                </div>
                <p className="text-3xl font-bold text-white" data-testid="stat-conversion">{stats.conversion_rate}%</p>
              </Card>
            </div>

            {/* Email Breakdown + Queue */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="bg-[#16213e] border-[#2a2a4a] p-5">
                <h3 className="text-lg font-semibold text-white mb-4">Emails by Type</h3>
                <div className="space-y-3">
                  {stats.by_type && Object.entries(stats.by_type).map(([type, count]) => (
                    <div key={type} className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm capitalize">{type.replace(/_/g, ' ')}</span>
                      <div className="flex items-center gap-3">
                        <div className="w-32 h-2 bg-[#2a2a4a] rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#6B7A5C] rounded-full"
                            style={{ width: `${Math.min((count / stats.total_sent) * 100, 100)}%` }}
                          />
                        </div>
                        <span className="text-white font-medium text-sm w-8 text-right">{count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
              <Card className="bg-[#16213e] border-[#2a2a4a] p-5">
                <h3 className="text-lg font-semibold text-white mb-4">Queue Status</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-sm flex items-center gap-2"><Clock className="w-4 h-4" /> Pending</span>
                    <span className="text-amber-400 font-medium">{stats.pending_queue}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-sm flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Sent</span>
                    <span className="text-emerald-400 font-medium">{stats.total_sent}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-sm flex items-center gap-2"><XCircle className="w-4 h-4" /> Failed</span>
                    <span className="text-red-400 font-medium">{stats.total_failed}</span>
                  </div>
                </div>
                <Button
                  onClick={handleProcessQueue}
                  className="mt-4 w-full bg-[#6B7A5C] hover:bg-[#5A6850] text-white text-sm"
                  data-testid="admin-process-queue-btn"
                >
                  Process Queue Now
                </Button>
              </Card>
            </div>
          </div>
        )}

        {/* Subscribers Tab */}
        {activeTab === 'subscribers' && (
          <div data-testid="admin-subscribers-panel">
            <Card className="bg-[#16213e] border-[#2a2a4a] overflow-hidden">
              <div className="p-4 border-b border-[#2a2a4a] flex justify-between items-center">
                <h3 className="text-lg font-semibold text-white">Subscribers ({subscribers.length})</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#2a2a4a]">
                      <th className="text-left p-3 text-gray-400 text-xs uppercase tracking-wider">Email</th>
                      <th className="text-left p-3 text-gray-400 text-xs uppercase tracking-wider">Source</th>
                      <th className="text-left p-3 text-gray-400 text-xs uppercase tracking-wider">Status</th>
                      <th className="text-left p-3 text-gray-400 text-xs uppercase tracking-wider">Purchased</th>
                      <th className="text-left p-3 text-gray-400 text-xs uppercase tracking-wider">Emails Sent</th>
                      <th className="text-left p-3 text-gray-400 text-xs uppercase tracking-wider">Subscribed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscribers.map((sub, i) => (
                      <tr key={i} className="border-b border-[#2a2a4a] hover:bg-[#1e2a4a]" data-testid={`subscriber-row-${i}`}>
                        <td className="p-3 text-white text-sm font-mono">{sub.email}</td>
                        <td className="p-3">
                          <div className="flex gap-1 flex-wrap">
                            {(sub.sources || []).map((s, j) => (
                              <span key={j} className="text-xs bg-[#2a2a4a] text-gray-300 px-2 py-0.5 rounded">{s}</span>
                            ))}
                          </div>
                        </td>
                        <td className="p-3">
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            sub.status === 'active' ? 'bg-emerald-900 text-emerald-300' : 'bg-red-900 text-red-300'
                          }`}>
                            {sub.status}
                          </span>
                        </td>
                        <td className="p-3">
                          {sub.has_purchased ? (
                            <CheckCircle className="w-4 h-4 text-emerald-400" />
                          ) : (
                            <XCircle className="w-4 h-4 text-gray-600" />
                          )}
                        </td>
                        <td className="p-3 text-gray-400 text-sm">{(sub.emails_sent || []).length}</td>
                        <td className="p-3 text-gray-400 text-sm">{formatDate(sub.subscribed_at)}</td>
                      </tr>
                    ))}
                    {subscribers.length === 0 && (
                      <tr>
                        <td colSpan={6} className="p-8 text-center text-gray-500">No subscribers yet</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* Email Logs Tab */}
        {activeTab === 'logs' && (
          <div data-testid="admin-logs-panel">
            <Card className="bg-[#16213e] border-[#2a2a4a] overflow-hidden">
              <div className="p-4 border-b border-[#2a2a4a]">
                <h3 className="text-lg font-semibold text-white">Email Logs ({logs.length})</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#2a2a4a]">
                      <th className="text-left p-3 text-gray-400 text-xs uppercase tracking-wider">Type</th>
                      <th className="text-left p-3 text-gray-400 text-xs uppercase tracking-wider">Recipient</th>
                      <th className="text-left p-3 text-gray-400 text-xs uppercase tracking-wider">Subject</th>
                      <th className="text-left p-3 text-gray-400 text-xs uppercase tracking-wider">Status</th>
                      <th className="text-left p-3 text-gray-400 text-xs uppercase tracking-wider">Sent At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log, i) => (
                      <tr key={i} className="border-b border-[#2a2a4a] hover:bg-[#1e2a4a]" data-testid={`log-row-${i}`}>
                        <td className="p-3">
                          <span className="text-xs bg-[#2a2a4a] text-gray-300 px-2 py-0.5 rounded capitalize">
                            {log.type?.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="p-3 text-white text-sm font-mono">{log.recipient}</td>
                        <td className="p-3 text-gray-400 text-sm max-w-[200px] truncate">{log.subject}</td>
                        <td className="p-3">
                          {log.status === 'sent' ? (
                            <span className="flex items-center gap-1 text-emerald-400 text-xs">
                              <CheckCircle className="w-3 h-3" /> Sent
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-red-400 text-xs">
                              <XCircle className="w-3 h-3" /> Failed
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-gray-400 text-sm">{formatDate(log.sent_at)}</td>
                      </tr>
                    ))}
                    {logs.length === 0 && (
                      <tr>
                        <td colSpan={5} className="p-8 text-center text-gray-500">No email logs</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* Queue Tab */}
        {activeTab === 'queue' && queue && (
          <div data-testid="admin-queue-panel">
            <div className="grid grid-cols-3 gap-4 mb-4">
              <Card className="bg-[#16213e] border-[#2a2a4a] p-4 text-center">
                <p className="text-amber-400 text-2xl font-bold">{queue.pending}</p>
                <p className="text-gray-400 text-sm">Pending</p>
              </Card>
              <Card className="bg-[#16213e] border-[#2a2a4a] p-4 text-center">
                <p className="text-emerald-400 text-2xl font-bold">{queue.sent}</p>
                <p className="text-gray-400 text-sm">Sent</p>
              </Card>
              <Card className="bg-[#16213e] border-[#2a2a4a] p-4 text-center">
                <p className="text-red-400 text-2xl font-bold">{queue.failed}</p>
                <p className="text-gray-400 text-sm">Failed</p>
              </Card>
            </div>

            <Card className="bg-[#16213e] border-[#2a2a4a] overflow-hidden">
              <div className="p-4 border-b border-[#2a2a4a] flex justify-between items-center">
                <h3 className="text-lg font-semibold text-white">Upcoming Scheduled Emails</h3>
                <Button
                  onClick={handleProcessQueue}
                  className="bg-[#6B7A5C] hover:bg-[#5A6850] text-white text-sm"
                  data-testid="admin-queue-process-btn"
                >
                  Process Now
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#2a2a4a]">
                      <th className="text-left p-3 text-gray-400 text-xs uppercase tracking-wider">Recipient</th>
                      <th className="text-left p-3 text-gray-400 text-xs uppercase tracking-wider">Email Type</th>
                      <th className="text-left p-3 text-gray-400 text-xs uppercase tracking-wider">Scheduled For</th>
                      <th className="text-left p-3 text-gray-400 text-xs uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(queue.upcoming || []).map((item, i) => (
                      <tr key={i} className="border-b border-[#2a2a4a] hover:bg-[#1e2a4a]">
                        <td className="p-3 text-white text-sm font-mono">{item.recipient}</td>
                        <td className="p-3">
                          <span className="text-xs bg-[#2a2a4a] text-gray-300 px-2 py-0.5 rounded capitalize">
                            {item.email_type?.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="p-3 text-gray-400 text-sm">{formatDate(item.scheduled_for)}</td>
                        <td className="p-3">
                          <span className="text-xs bg-amber-900 text-amber-300 px-2 py-0.5 rounded">{item.status}</span>
                        </td>
                      </tr>
                    ))}
                    {(!queue.upcoming || queue.upcoming.length === 0) && (
                      <tr>
                        <td colSpan={4} className="p-8 text-center text-gray-500">No scheduled emails</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* Trigger Tab */}
        {activeTab === 'trigger' && (
          <div data-testid="admin-trigger-panel">
            <Card className="bg-[#16213e] border-[#2a2a4a] p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Manually Send Emails</h3>
              <p className="text-gray-400 text-sm mb-6">
                Use this to test or manually trigger any email in the system. Emails go through SendGrid live.
              </p>

              <div className="flex gap-3 mb-6">
                <Input
                  type="email"
                  value={triggerEmail}
                  onChange={(e) => setTriggerEmail(e.target.value)}
                  placeholder="recipient@email.com"
                  className="bg-[#1a1a2e] border-[#3a3a5a] text-white placeholder-gray-500"
                  data-testid="admin-trigger-email-input"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button
                  onClick={() => handleSendEmail('send-free-guide')}
                  className="bg-[#6B7A5C] hover:bg-[#5A6850] text-white justify-start"
                  data-testid="admin-send-free-guide-btn"
                >
                  <Mail className="w-4 h-4 mr-2" /> Free Guide Email
                </Button>
                <Button
                  onClick={() => handleSendEmail('send-nurture-day2')}
                  className="bg-[#2a2a4a] hover:bg-[#3a3a5a] text-gray-300 border border-[#3a3a5a] justify-start"
                  data-testid="admin-send-nurture2-btn"
                >
                  <Send className="w-4 h-4 mr-2" /> Nurture Day 2
                </Button>
                <Button
                  onClick={() => handleSendEmail('send-nurture-day5')}
                  className="bg-[#2a2a4a] hover:bg-[#3a3a5a] text-gray-300 border border-[#3a3a5a] justify-start"
                  data-testid="admin-send-nurture5-btn"
                >
                  <Send className="w-4 h-4 mr-2" /> Nurture Day 5
                </Button>
                <Button
                  onClick={() => handleSendEmail('send-nurture-day7')}
                  className="bg-[#2a2a4a] hover:bg-[#3a3a5a] text-gray-300 border border-[#3a3a5a] justify-start"
                  data-testid="admin-send-nurture7-btn"
                >
                  <Send className="w-4 h-4 mr-2" /> Nurture Day 7
                </Button>
                <Button
                  onClick={() => handleSendEmail('send-post-purchase-followup')}
                  className="bg-[#C17855] hover:bg-[#B06745] text-white justify-start"
                  data-testid="admin-send-followup-btn"
                >
                  <Send className="w-4 h-4 mr-2" /> Post-Purchase Follow-up
                </Button>
                <Button
                  onClick={() => handleSendEmail('send-cross-sell')}
                  className="bg-[#C17855] hover:bg-[#B06745] text-white justify-start"
                  data-testid="admin-send-crosssell-btn"
                >
                  <Send className="w-4 h-4 mr-2" /> Cross-Sell Upsell
                </Button>
              </div>

              {triggerResult && (
                <div className="mt-4 p-3 bg-[#1a1a2e] border border-[#3a3a5a] rounded-lg" data-testid="admin-trigger-result">
                  <p className="text-sm text-gray-300 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-400" />
                    {triggerResult}
                  </p>
                </div>
              )}
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
